import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Megaphone, BadgePercent, Rocket, Calendar, Check, ChevronRight, X } from 'lucide-react';
import { loadLastCrearAnuncioState, mergeLastCrearAnuncioState } from '../utils/crearAnuncioStorage';

export const SeleccionarObjetivoAnuncio: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const idsParam = params.get('ids') || '';
  const ids = useMemo(
    () => idsParam.split(',').map((s) => s.trim()).filter(Boolean),
    [idsParam]
  );
  const modo = (params.get('modo') as 'rapido' | 'personalizado') || 'rapido';

  const savedStateRef = useRef(loadLastCrearAnuncioState());

  type ObjetivoKey = 'mostrar' | 'oferta' | 'lanzamiento' | 'evento';
  const objetivoParam = params.get('objetivo');
  const allowedObjetivos: ObjetivoKey[] = ['mostrar', 'oferta', 'lanzamiento', 'evento'];
  const initialObjetivo: ObjetivoKey = useMemo(() => {
    const paramObjetivo = objetivoParam as ObjetivoKey | null;
    if (paramObjetivo && allowedObjetivos.includes(paramObjetivo)) return paramObjetivo;
    const saved = savedStateRef.current?.objetivo as ObjetivoKey | undefined;
    if (saved && allowedObjetivos.includes(saved)) return saved;
    return 'mostrar';
  }, [objetivoParam]);
  const [objetivo, setObjetivo] = useState<ObjetivoKey | null>(initialObjetivo);
  const [offerDialogOpen, setOfferDialogOpen] = useState(false);
  const savedOfferDetails = savedStateRef.current?.detallesOferta || '';
  const [offerDetails, setOfferDetails] = useState(savedOfferDetails);

  // Scroll to top on mount (iOS Safari fix)
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  const objetivos: Array<{
    key: ObjetivoKey;
    title: string;
    description: string;
    Icon: React.ComponentType<any>;
    colorBg: string;
    colorText: string;
  }> = [
    {
      key: 'mostrar',
      title: 'Mostrar mi producto/marca',
      description: 'Aumentá el reconocimiento de tu marca o destacá un producto.',
      Icon: Megaphone,
      colorBg: 'bg-blue-50',
      colorText: 'text-blue-600',
    },
    {
      key: 'oferta',
      title: 'Oferta/Promoción',
      description: 'Comunicá descuentos, 2x1, promociones por tiempo limitado, etc.',
      Icon: BadgePercent,
      colorBg: 'bg-emerald-50',
      colorText: 'text-emerald-600',
    },
    {
      key: 'lanzamiento',
      title: 'Nuevo lanzamiento',
      description: 'Presentá un nuevo producto, colección o servicio.',
      Icon: Rocket,
      colorBg: 'bg-violet-50',
      colorText: 'text-violet-600',
    },
    {
      key: 'evento',
      title: 'Evento',
      description: 'Anunciá un evento, workshop o live con fecha y lugar.',
      Icon: Calendar,
      colorBg: 'bg-orange-50',
      colorText: 'text-orange-600',
    },
  ];

  const handleSelect = (key: ObjetivoKey) => {
    setObjetivo(key);
    if (key === 'oferta' || key === 'evento') {
      setOfferDialogOpen(true);
    } else {
      setOfferDetails('');
    }
  };

  useEffect(() => {
    const trimmedDetails = offerDetails.trim();
    let detallesValue: string | undefined | null;
    if (objetivo === 'oferta' || objetivo === 'evento') {
      detallesValue = trimmedDetails ? trimmedDetails : undefined;
    } else {
      detallesValue = null;
    }

    mergeLastCrearAnuncioState({
      modo,
      selectedVariantIds: ids,
      objetivo: objetivo || undefined,
      detallesOferta: detallesValue ?? undefined,
      ...(detallesValue === null ? { detallesOferta: null } : {}),
    });
  }, [ids, modo, objetivo, offerDetails]);

  const handleContinue = () => {
    if (!objetivo) return;
    const qp = new URLSearchParams();
    qp.set('ids', ids.join(','));
    qp.set('modo', modo);
    qp.set('objetivo', objetivo);
    const trimmedDetails = offerDetails.trim();
    if ((objetivo === 'oferta' || objetivo === 'evento') && trimmedDetails) {
      qp.set('detalles_oferta', trimmedDetails);
    }
    const next = modo === 'rapido'
      ? '/redes-sociales/crear-anuncio/seleccionar-formato'
      : '/redes-sociales/crear-anuncio/configurar';
    navigate(`${next}?${qp.toString()}`);
  };

  return (
    <div className="p-4 sm:p-8 sm:pb-0 max-w-4xl mx-auto min-h-[100dvh] md:min-h-screen bg-white sm:bg-transparent">
      {/* Header + progress */}
      <div className="sm:bg-white sm:rounded-2xl sm:shadow-sm p-0 sm:p-6">
        {/* Mobile header */}
        <div className="sm:hidden mt-2 mb-4 text-center">
          <h1 className="text-sm font-semibold text-gray-900">Seleccioná el objetivo</h1>
          <p className="text-sm text-gray-600 mt-3">Modo {modo === 'personalizado' ? 'personalizado' : 'automático'} · {ids.length} productos seleccionados</p>
        </div>

        {/* Desktop header */}
        <div className="hidden sm:flex items-start justify-between mb-6">
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Seleccioná el objetivo</h1>
            <p className="text-sm text-gray-600 mt-1">Modo {modo === 'personalizado' ? 'personalizado' : 'automático'} · {ids.length} productos seleccionados</p>
          </div>
        </div>
        <div className="mt-4">
          <div className="w-full h-1 bg-gray-200 rounded-full">
            <div className="h-1 bg-blue-500 rounded-full" style={{ width: '50%' }} />
          </div>
        </div>

        {/* Objetivos */}
        <div className="mt-6 grid grid-cols-1 gap-3">
          {objetivos.map(({ key, title, description, Icon, colorBg, colorText }) => {
            const selected = objetivo === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => handleSelect(key)}
                className={`w-full text-left border ${selected ? 'border-blue-500 ring-2 ring-blue-500' : 'border-gray-200'} rounded-2xl p-4 flex items-start gap-4 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors`}
              >
                <div className={`shrink-0 ${colorBg} p-2 rounded-md`}>
                  <Icon className={`w-6 h-6 ${colorText}`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h2 className="text-base sm:text-lg font-semibold text-gray-900">{title}</h2>
                    {selected && (
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-500 text-white">
                        <Check className="w-4 h-4" />
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{description}</p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Footer actions (desktop) */}
        <div className="mt-6 hidden sm:flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
          >
            Volver
          </button>

          <button
            type="button"
            disabled={!objetivo}
            className={`inline-flex items-center gap-2 px-4 h-10 rounded-full shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-sm ${!objetivo ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600 text-white'}`}
            onClick={handleContinue}
          >
            Continuar
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Mobile spacer for fixed bottom bar */}
      <div className="sm:hidden" style={{ height: 'calc(env(safe-area-inset-bottom, 0px) + 64px)' }} />

      {/* Mobile bottom bar */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 z-30">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
          >
            Volver
          </button>
          <button
            type="button"
            disabled={!objetivo}
            className={`inline-flex items-center gap-2 px-4 h-10 rounded-full shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-sm ${!objetivo ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600 text-white'}`}
            onClick={handleContinue}
          >
            Continuar
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Offer details dialog */}
      {offerDialogOpen && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl shadow-lg p-4 sm:p-6">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{objetivo === 'evento' ? 'Detalles del evento' : 'Detalles de la oferta'}</h3>
                <p className="text-sm text-gray-600 mt-1">
                  {objetivo === 'evento'
                    ? 'Fecha, hora, lugar, modalidad (presencial/online) y cualquier dato importante.'
                    : '¿Qué tipo de oferta es? ¿Se hace por algún motivo en especial?'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOfferDialogOpen(false)}
                className="p-2 text-gray-500 hover:text-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                aria-label="Cerrar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mt-4">
              <textarea
                rows={4}
                value={offerDetails}
                onChange={(e) => setOfferDetails(e.target.value)}
                placeholder={objetivo === 'evento'
                  ? 'Ej: Sábado 21/09, 18:00 hs, Av. Siempre Viva 123. Cupos limitados. Entrada libre. Transmisión por Instagram Live.'
                  : 'Ej: 20% OFF por aniversario, 2x1 en toda la tienda este fin de semana, envío gratis por vuelta al cole, etc.'}
                className="w-full rounded-md border border-gray-300 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              />
            </div>
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                className="px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                onClick={() => setOfferDialogOpen(false)}
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={offerDetails.trim().length === 0}
                className={`inline-flex items-center px-4 h-10 rounded-full shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-sm ${offerDetails.trim().length === 0 ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600 text-white'}`}
                onClick={() => setOfferDialogOpen(false)}
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SeleccionarObjetivoAnuncio;
